/**
 * V111: MM관리 > MM현황 페이지
 *
 * 팀 단위 MM 현황 조회
 * - 팀별 인력현황 (전체인력, 투입인력, 유휴인력, 중복투입)
 * - 프로젝트별 MD 계산 (기준MD, 실투입MD)
 * - 야근 시간 집계
 *
 * 접근 권한: role_manager, role_exec, admin
 */

import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, AlertCircle, Loader2 } from 'lucide-react';
import { useMmOverviewData } from '../../shared/hooks/useMmOverviewData';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { getCurrentMonth } from '../../shared/types/mm';
import { MmMonthSelector } from './components/MmMonthSelector';
import { MmTeamSummaryCard } from './components/MmTeamSummaryCard';
import { MmProjectTable } from './components/MmProjectTable';

/**
 * MM현황 페이지
 */
export function MMOverviewPage() {
  const [searchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

  // V131.3: URL ?debugPerf=1 로 BE 성능 정보 요청 가능
  const debugPerf = useMemo(() => searchParams.get('debugPerf') === '1', [searchParams]);

  // V314: 통합어드민 국가 선택
  // V73: KR 임원/조직장도 국가 선택 허용
  const authContext = useAuthContext();
  const { user: currentUser } = useCurrentUser();
  const userCompanyCode = (currentUser?.profile as any)?.companyCode as 'KR' | 'CN' | undefined;
  const roleIds = useMemo(
    () => authContext?.roleAssignments?.map((ra) => ra.roleId) || [],
    [authContext?.roleAssignments]
  );
  const hasGlobalScopeAccess = useMemo(() => {
    return roleIds.includes('system_admin') || roleIds.includes('role_global');
  }, [roleIds]);
  // V73: KR + (role_exec | role_manager) → 국가 선택 UI 노출
  const isKrPrivileged = useMemo(() => {
    return userCompanyCode === 'KR' && (
      roleIds.includes('role_exec') || roleIds.includes('role_manager')
    );
  }, [userCompanyCode, roleIds]);
  const canSelectCountry = hasGlobalScopeAccess || isKrPrivileged;
  const [filterCompanyCode, setFilterCompanyCode] = useState<'KR' | 'CN'>('KR');

  const { data, teams, workingDays, holidays, isLoading, error, refetch, resolvedCompanyCode } = useMmOverviewData({
    month: selectedMonth,
    debugPerf,
    // V314/V73: 국가 선택 권한 있는 사용자는 선택한 국가 전달
    ...(canSelectCountry && { companyCode: filterCompanyCode }),
    // V73.3: MM현황 → KR role_manager ALL 승격 적용
    viewMode: 'overview',
  });

  return (
    <div style={{ padding: '24px' }}>
      {/* 헤더 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
        }}
      >
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'var(--color-gray-800)',
          }}
        >
          MM 현황
        </h1>

        {/* V314/V73: 국가 선택 (통합어드민 / KR 임원 / KR 조직장) */}
        {canSelectCountry && (
          <select
            value={filterCompanyCode}
            onChange={(e) => setFilterCompanyCode(e.target.value as 'KR' | 'CN')}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white',
            }}
          >
            <option value="KR">KR (한국)</option>
            <option value="CN">CN (대련)</option>
          </select>
        )}
      </div>

      {/* 월 선택기 + V54.10: 공휴일 정보를 상단으로 이동 */}
      <MmMonthSelector
        month={selectedMonth}
        onMonthChange={setSelectedMonth}
        workingDays={workingDays}
        holidays={holidays}
        disabled={isLoading}
      />

      {/* 로딩 상태 */}
      {isLoading && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
            color: 'var(--color-gray-500)',
          }}
        >
          <Loader2 size={32} className="animate-spin" style={{ marginBottom: '12px' }} />
          <span>데이터를 불러오는 중...</span>
        </div>
      )}

      {/* 에러 상태 */}
      {error && !isLoading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            backgroundColor: 'var(--color-red-50)',
            border: '1px solid var(--color-red-200)',
            borderRadius: '8px',
            color: 'var(--color-red-700)',
          }}
        >
          <AlertCircle size={20} />
          <div>
            <div style={{ fontWeight: 600 }}>데이터 조회 실패</div>
            <div style={{ fontSize: '13px' }}>{error.message}</div>
          </div>
          <button
            onClick={() => refetch()}
            style={{
              marginLeft: 'auto',
              padding: '8px 16px',
              backgroundColor: 'var(--color-red-100)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            다시 시도
          </button>
        </div>
      )}

      {/* 팀 목록 */}
      {!isLoading && !error && (
        <>
          {teams.length === 0 ? (
            <div
              style={{
                padding: '48px',
                textAlign: 'center',
                color: 'var(--color-gray-500)',
                backgroundColor: 'var(--color-gray-50)',
                borderRadius: '8px',
              }}
            >
              <BarChart3 size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <div style={{ fontSize: '16px', fontWeight: 500 }}>
                표시할 팀이 없습니다.
              </div>
              <div style={{ fontSize: '14px', marginTop: '8px' }}>
                권한이 있는 팀의 프로젝트가 없거나 접근 권한이 없습니다.
              </div>
              {/* V233: 진단 정보 (개발환경에서만) */}
              {data?.debugInfo && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: 'var(--color-yellow-50)',
                  border: '1px solid var(--color-yellow-200)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: 'var(--color-gray-600)',
                  textAlign: 'left',
                  fontFamily: 'monospace',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>[V233 진단]</div>
                  <div>companyCode: {data.debugInfo.companyCode}</div>
                  <div>scopeMode: {data.debugInfo.scopeMode}</div>
                  <div>orgUnits(type=team): {data.debugInfo.totalOrgTeams}</div>
                  <div>scope 내 팀: {data.debugInfo.scopeTeamCount}</div>
                  <div>MM 활성 팀: {data.debugInfo.mmEnabledCount}</div>
                  <div>userRoles: [{data.debugInfo.userRoles?.join(', ')}]</div>
                  <div>userOrgUnitId: {data.debugInfo.userOrgUnitId || 'null'}</div>
                </div>
              )}
            </div>
          ) : (
            teams.map((team) => (
              <div
                key={team.teamId}
                style={{
                  marginBottom: '32px',
                  padding: '20px',
                  backgroundColor: 'white',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                {/* 팀 인력현황 카드 */}
                <MmTeamSummaryCard team={team} contextCompanyCode={resolvedCompanyCode} />

                {/* 프로젝트 테이블 */}
                <div
                  style={{
                    backgroundColor: 'var(--color-gray-50)',
                    borderRadius: '8px',
                    padding: '16px',
                  }}
                >
                  {/* V116.1.6: 프로젝트 현황 제목이 MmProjectTable 내부로 이동 */}
                  {/* V207: monthWorkingDays 전달 (WD 기반 MM 환산) */}
                  <MmProjectTable
                    projects={team.projects}
                    teamId={team.teamId}
                    month={selectedMonth}
                    isEditable={false}
                    monthWorkingDays={workingDays}
                  />
                </div>
              </div>
            ))
          )}
        </>
      )}

    </div>
  );
}
